import React, { useEffect, useMemo, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import ApiBaseURL from '../ApiBaseUrl/ApiBaseURL';
import { useNavigate } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const DashboardTripByShift = ({
    vehicleTypeId = null,
    vendorId = null,
    driverId = null,
    vehicleId = null,
    period = null
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [shiftData, setShiftData] = useState({ A: [], B: [], C: [] });
    const [labels, setLabels] = useState([]);
    const [selectedRange, setSelectedRange] = useState(period);

    const navigate = useNavigate();

    const filterOptions = [
        { label: 'Year-25', value: 'y-25' },
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: 'Week', value: 'week' },
        { label: 'Current Month', value: 'cm' },
        { label: 'Prev. Month', value: 'pm' },
        { label: '3M', value: '3m' },
        { label: '6M', value: '6m' },
        { label: '9M', value: '9m' },
    ];

    const monthNames = [
        '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    useEffect(() => {
        handleGetTripByShift();
    }, [vehicleTypeId, vendorId, driverId, vehicleId, selectedRange, period]);

    const handleGetTripByShift = async () => {
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('token');

        if (!userId || !token) {
            toast.error('Missing user credentials');
            return;
        }

        try {
            const response = await fetch(`${ApiBaseURL}/dashboard/v1/get/trip-by-shift`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    userId,
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    vehicleTypeId,
                    vendorId,
                    driverId,
                    vehicleId,
                    // period: selectedRange
                    period
                })
            });

            const result = await response.json();

            if (response.ok && result?.statusDescription?.statusCode === 200) {
                const rawData = result.data;
                const tempData = { A: [], B: [], C: [] };
                const tempLabels = [];

                for (const key of Object.keys(rawData).sort()) {
                    const entries = rawData[key];

                    const label = /^\d{4}-\d{2}-\d{2}$/.test(key)
                        ? new Date(key).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                        : monthNames[parseInt(key)] || key;

                    tempLabels.push(label);

                    let shiftA = 0, shiftB = 0, shiftC = 0;

                    for (const entry of entries) {
                        if (entry.shift === 'A') shiftA += entry.totalTripCount;
                        if (entry.shift === 'B') shiftB += entry.totalTripCount;
                        if (entry.shift === 'C') shiftC += entry.totalTripCount;
                    }

                    tempData.A.push(shiftA);
                    tempData.B.push(shiftB);
                    tempData.C.push(shiftC);
                }

                setLabels(tempLabels);
                setShiftData(tempData);
            } else if ([401, 403].includes(result.statusDescription.statusCode)) {
                toast.error(result.statusDescription.description || "Unauthorized access. Redirecting...");
                localStorage.clear();

                setTimeout(() => {
                    window.location.replace('/');
                }, 1500);
            } else {
                console.log(result.statusDescription?.description || 'Failed to fetch shift data');
            }
        } catch (err) {
            toast.error('Error fetching data: ' + err.message);
        }
    };

    const handleFilterSelect = (range) => {
        setSelectedRange(range);
        setShowDropdown(false);
    };

    const chartData = {
        labels,
        datasets: [
            {
                label: 'Shift-A',
                data: shiftData.A,
                backgroundColor: 'rgba(54, 162, 235, 0.7)',
            },
            {
                label: 'Shift-B',
                data: shiftData.B,
                backgroundColor: 'rgba(255, 99, 132, 0.7)',
            },
            {
                label: 'Shift-C',
                data: shiftData.C,
                backgroundColor: 'rgba(255, 206, 86, 0.7)',
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                // text: 'Trip Count by Shift',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Trips'
                },
                ticks: {
                    callback: function (value) {
                        if (value >= 1_00_00_000) return (value / 1_00_00_000).toFixed(1).replace(/\.0$/, '') + 'Cr';
                        if (value >= 1_00_000) return (value / 1_00_000).toFixed(1).replace(/\.0$/, '') + 'L';
                        if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
                        if (value >= 1_000) return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
                        return value;
                    }
                }
            },
            x: {
                title: {
                    display: true,
                    // text: 'Period'
                }
            }
        },
    };

    const totals = useMemo(() => {
        return Object.values(shiftData)
            .flat()
            .reduce(
                (acc, item) => {
                    acc.totalTripCount += item.totalTripCount || 0;
                    acc.softTripCount += item.softTripCount || 0;
                    acc.hardTripCount += item.hardTripCount || 0;
                    return acc;
                },
                { totalTripCount: 0, softTripCount: 0, hardTripCount: 0 }
            );
    }, [shiftData]);

    return (
        <div className="card shadow-sm my-0">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">Trip by Shift</h5>
                    <div style={{ position: 'relative' }}>
                        <i
                            className="fa-solid fa-filter d-none"
                            style={{ cursor: 'pointer', fontSize: '16px' }}
                            onClick={() => setShowDropdown(!showDropdown)}
                        ></i>
                        {showDropdown && (
                            <div
                                className="dropdown-menu show"
                                style={{ position: 'absolute', right: 0, zIndex: 999 }}
                            >
                                {filterOptions.map((opt) => (
                                    <button
                                        key={opt.value}
                                        className="dropdown-item"
                                        onClick={() => handleFilterSelect(opt.value)}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <Bar data={chartData} options={chartOptions} />
            </div>

            {/* <div className="row mb-3 text-center">
                <div className="col-md-4">
                    <h6 className="mb-1">Total Trips</h6>
                    <p className="fw-bold text-primary mb-0">{totals.totalTripCount}</p>
                </div>
                <div className="col-md-4">
                    <h6 className="mb-1">Soft Trips</h6>
                    <p className="fw-bold text-success mb-0">{totals.softTripCount}</p>
                </div>
                <div className="col-md-4">
                    <h6 className="mb-1">Hard Trips</h6>
                    <p className="fw-bold text-warning mb-0">{totals.hardTripCount}</p>
                </div>
            </div> */}

        </div>
    );
};

export default DashboardTripByShift;